import { createReference, MedplumClient } from "@medplum/core";
import type {
  Encounter,
  Practitioner,
  Questionnaire,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  Reference,
} from "@medplum/fhirtypes";

import { Dispatch } from "@reduxjs/toolkit";
import isEqual from "lodash/isEqual";

import { questionnaireURLBasedOnEncounterType } from "@/encounters/constants/questionnaire";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import { Extensions } from "@/fhir/enums/extensions";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import {
  QuiltedHealthCodeSystems,
  QuiltedHealthUsageContextTypes,
} from "@/fhir/enums/quilted-health";
import { startResourcePolling } from "@/store/slices/resource-polling";
import { PoolingKeys } from "@/types/pooling";
import { getResourcesFromBundle, joinReferenceString, setExtensionValue } from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";

import { PhysicalExamOption } from "../types/physical-exams";
import { createPhysicalExamSectionQuestionnaireResponseItem } from "../utils/physical-exams";
import { PhysicalExamForm } from "../zod-schemas/physical-exams";

/**
 * Get the main questionnaire URL for an encounter based on its service type
 */
export function getMainQuestionnaireUrlFromEncounter(encounter: Encounter): string | undefined {
  const serviceTypeDisplay = encounter.serviceType?.coding?.[0]?.display;
  return questionnaireURLBasedOnEncounterType[
    serviceTypeDisplay as keyof typeof questionnaireURLBasedOnEncounterType
  ];
}

export async function createPhysicalExamQuestionnaireResponse<T extends PhysicalExamForm>(
  medplum: MedplumClient,
  questionnaire: Questionnaire,
  encounterReference: Reference<Encounter>,
  practitionerReference: Reference<Practitioner>,
  clientId: string,
  initialValues: T,
  values: T,
  mainQuestionnaireUrl?: string,
): Promise<QuestionnaireResponse | undefined> {
  if (isEqual(initialValues, values)) return;

  let parentVersionId: string | undefined;

  // Get the parent questionnaire response versionId if mainQuestionnaireUrl is provided
  if (mainQuestionnaireUrl && encounterReference.reference) {
    const encounterId = encounterReference.reference.split("/")[1];
    const [mainQuestionnaireResponse] = await fetchAmendmentHistoryByQuestionnaireUrl(
      medplum,
      mainQuestionnaireUrl,
      encounterId,
    );
    parentVersionId = mainQuestionnaireResponse?.[0]?.meta?.versionId;
  }

  const questionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    status: "completed",
    subject: { reference: joinReferenceString("Patient", clientId) },
    author: practitionerReference,
    questionnaire: joinReferenceString("Questionnaire", questionnaire.id!),
    encounter: encounterReference,
    item: getPhysicalExamQuestionnaireResponseItem(values, questionnaire),
    extension: parentVersionId
      ? setExtensionValue([], Extensions.QuestionnaireResponseParentVersionId, {
          valueString: parentVersionId,
        })
      : [],
  };

  if (values.notes) {
    questionnaireResponse.item?.push({
      linkId: "notes",
      answer: [{ valueString: values.notes }],
    });
  }

  if (!questionnaireResponse.item?.length) {
    // We shouldn't create a questionnaire response if there are no answers to the PE
    return undefined;
  }

  return await medplum.createResource(questionnaireResponse);
}

function getPhysicalExamQuestionnaireResponseItem<T extends PhysicalExamForm>(
  values: T,
  questionnaire: Questionnaire,
) {
  return Object.entries(values.sections)
    .map(([sectionId, section]) =>
      createPhysicalExamSectionQuestionnaireResponseItem(sectionId, section, questionnaire),
    )
    .filter(Boolean) as QuestionnaireResponseItem[];
}

export async function fetchPhysicalExamsOptions(
  medplum: MedplumClient,
  encounterType: string,
): Promise<PhysicalExamOption[]> {
  const response = await medplum.searchResources("Questionnaire", {
    "context-type": QuiltedHealthUsageContextTypes.PhysicalExams,
    _tag: `${encounterType},all`,
    status: "active",
    _elements: "url,title,useContext,meta",
    _count: 1000,
    _sort: "title",
  });

  const allPhysicalExams = getResourcesFromBundle("Questionnaire", response.bundle);

  return allPhysicalExams.map((physicalExam) => ({
    id: physicalExam.id!,
    title: physicalExam.title!,
    url: physicalExam.url!,
  }));
}

export async function addPhysicalExamToEncounter(
  medplum: MedplumClient,
  encounter: Encounter,
  physicalExamUrl: string,
) {
  const versionId = encounter.meta!.versionId!;
  try {
    return await medplum.updateResource(
      {
        ...encounter,
        extension: [
          ...(encounter.extension ?? []),
          { url: Extensions.EncounterPhysicalExam, valueUri: physicalExamUrl },
        ],
      },
      { headers: { "If-Match": versionId } },
    );
  } catch (error) {
    showSystemErrorNotification();
    throw error;
  }
}

export async function deletePhysicalExamFromEncounter(
  medplum: MedplumClient,
  dispatch: Dispatch,
  clientId: string,
  encounter: Encounter,
  practitionerReference: Reference<Practitioner>,
  physicalExamCode: string,
  physicalExamUrl: string,
) {
  try {
    const questionnaire = await medplum.searchOne("Questionnaire", {
      url: Questionnaires.DeletePhysicalExam,
    });

    if (!questionnaire) {
      throw new Error('"DeletePhysicalExam" questionnaire not found');
    }

    const questionnaireResponse: QuestionnaireResponse = {
      resourceType: "QuestionnaireResponse",
      status: "completed",
      subject: { reference: joinReferenceString("Patient", clientId) },
      author: practitionerReference,
      questionnaire: joinReferenceString("Questionnaire", questionnaire.id!),
      encounter: createReference(encounter),
      item: [
        {
          linkId: "physicalExamUrl",
          answer: [{ valueUri: physicalExamUrl }],
        },
        {
          linkId: "physicalExamCoding",
          answer: [
            {
              valueCoding: {
                code: physicalExamCode,
                system: QuiltedHealthCodeSystems.PhysicalExams,
              },
            },
          ],
        },
      ],
    };

    await medplum.createResource(questionnaireResponse);

    dispatch(
      startResourcePolling({
        key: PoolingKeys.EncounterPhysicalExams,
        versionId: encounter.meta!.versionId!,
      }),
    );
  } catch (error) {
    showSystemErrorNotification();
    throw error;
  }
}

export async function amendPhysicalExamQuestionnaireResponse<T extends PhysicalExamForm>(
  medplum: MedplumClient,
  questionnaire: Questionnaire,
  encounterReference: Reference<Encounter>,
  practitionerReference: Reference<Practitioner>,
  clientId: string,
  initialValues: T,
  values: T,
  mainQuestionnaireUrl?: string,
) {
  const questionnaireResponse = await medplum.searchOne("QuestionnaireResponse", {
    subject: joinReferenceString("Patient", clientId),
    questionnaire: joinReferenceString("Questionnaire", questionnaire.id!),
    encounter: encounterReference.reference,
    _sort: "-_lastUpdated",
  });

  if (!questionnaireResponse) {
    throw new Error(`QuestionnaireResponse not found for Questionnaire: ${questionnaire.id}`);
  }

  let parentVersionId: string | undefined;

  // Get the parent questionnaire response versionId if mainQuestionnaireUrl is provided
  if (mainQuestionnaireUrl && encounterReference.reference) {
    const encounterId = encounterReference.reference.split("/")[1];
    const [mainQuestionnaireResponse] = await fetchAmendmentHistoryByQuestionnaireUrl(
      medplum,
      mainQuestionnaireUrl,
      encounterId,
    );
    parentVersionId = mainQuestionnaireResponse?.[0]?.meta?.versionId;
  }

  const updatedQuestionnaireResponse = {
    ...questionnaireResponse,
    status: "amended",
    item: getPhysicalExamQuestionnaireResponseItem(values, questionnaire),
    authored: new Date().toISOString(),
    extension: parentVersionId
      ? setExtensionValue(
          questionnaireResponse.extension,
          Extensions.QuestionnaireResponseParentVersionId,
          { valueString: parentVersionId },
        )
      : (questionnaireResponse.extension || []).filter(
          (ext) => ext.url !== Extensions.QuestionnaireResponseParentVersionId,
        ),
  } as QuestionnaireResponse;

  if (values.notes) {
    updatedQuestionnaireResponse.item?.push({
      linkId: "notes",
      answer: [{ valueString: values.notes }],
    });
  }

  try {
    await medplum.updateResource(updatedQuestionnaireResponse, {
      headers: { "If-Match": questionnaireResponse.meta!.versionId! },
    });
  } catch (error) {
    showSystemErrorNotification();
    throw error;
  }
}
